import { useCallback, useEffect, useState } from 'react';
import {
  WORKWISE_DARK_MODE_KEY,
  MILEPAD_DARK_MODE_KEY,
  isDarkModeEnabled,
  setDarkModeEnabled,
} from './darkMode';

const THEME_ATTRIBUTE = 'data-theme';

const subscribers = new Set();
const themeConsumers = new Set();
let current = false;

const applyTheme = () => {
  const root = document.documentElement;
  if (current && themeConsumers.size > 0) {
    root.setAttribute(THEME_ATTRIBUTE, 'dark');
  } else {
    root.removeAttribute(THEME_ATTRIBUTE);
    root.style.backgroundColor = '';
  }
};

const notify = () => {
  subscribers.forEach((listener) => listener(current));
};

const handleStorage = (event) => {
  if (event.key !== null && event.key !== WORKWISE_DARK_MODE_KEY && event.key !== MILEPAD_DARK_MODE_KEY) return;
  current = isDarkModeEnabled();
  applyTheme();
  notify();
};

const getSnapshot = () => (subscribers.size > 0 ? current : isDarkModeEnabled());

const subscribe = (listener, applyThemeAttribute) => {
  if (subscribers.size === 0) {
    current = isDarkModeEnabled();
    window.addEventListener('storage', handleStorage);
  }
  subscribers.add(listener);
  if (applyThemeAttribute) themeConsumers.add(listener);
  applyTheme();

  return () => {
    subscribers.delete(listener);
    themeConsumers.delete(listener);
    applyTheme();
    if (subscribers.size === 0) {
      window.removeEventListener('storage', handleStorage);
      document.documentElement.removeAttribute(THEME_ATTRIBUTE);
    }
  };
};

const setDark = (next) => {
  current = next;
  setDarkModeEnabled(next);
  applyTheme();
  notify();
};

export function useDarkMode({ applyTheme: applyThemeAttribute = false } = {}) {
  const [dark, setDarkState] = useState(getSnapshot);

  useEffect(() => subscribe(setDarkState, applyThemeAttribute), [applyThemeAttribute]);

  const toggle = useCallback(() => {
    setDark(!current);
  }, []);

  return { dark, toggle };
}

export default useDarkMode;
